import { isEditableMetadataPath } from "./isEditableMetadataPath";

/**
 * Removes derived metadata for no-op comparison, retaining complete variant catalogue contents.
 *
 * @param value - Domain data, nested property or array being compared for unified history.
 * @param path - Decoded property segments locating the current value within editable data.
 * @param excludedFields - Comparison-specific metadata names, or the default unified history exclusions.
 * @param originals - Optional map populated with each stripped object or array and its complete source value.
 * @returns Comparable data with metadata omitted only outside patch catalogues.
 */
export const omitStaticMetadata = (
  value: unknown,
  path: string[] = [],
  excludedFields?: ReadonlySet<string>,
  originals?: Map<unknown, unknown>,
): unknown => {
  if (path.includes("allVariantPatches")) return value;
  if (Array.isArray(value)) {
    const stripped = value.map((item: unknown, index: number) =>
      omitStaticMetadata(item, [...path, String(index)], excludedFields, originals),
    );
    originals?.set(stripped, value);
    return stripped;
  }
  if (value !== null && typeof value === "object") {
    const entries: [string, unknown][] = Object.entries(value);
    const stripped = Object.fromEntries(
      entries
        .filter(
          ([key, item]) =>
            item !== undefined && !isEditableMetadataPath([...path, key], excludedFields),
        )
        .map(([key, item]) => [
          key,
          omitStaticMetadata(item, [...path, key], excludedFields, originals),
        ]),
    );
    originals?.set(stripped, value);
    return stripped;
  }
  return value;
};
